import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/misc';
import { api, isApiError } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { PasswordForm } from './password-form';

/**
 * The screen a user lands on when an administrator has reset their password.
 *
 * It replaces the application rather than sitting inside it: no navigation, no outlet, nothing to
 * click past. That is a courtesy to the user, not the enforcement — the server answers 403
 * `PASSWORD_CHANGE_REQUIRED` to every other route until the password is replaced, so a client
 * that skipped this screen would simply find nothing works.
 */
export function MustChangePasswordPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentError, setCurrentError] = useState<string | undefined>();

  const submit = async (newPassword: string) => {
    setCurrentError(undefined);
    try {
      await api('/me/password', { method: 'POST', body: { currentPassword, newPassword } });
      toast.success(t('auth.mustChangeDone'));
      // Everything was refused while the flag was set, so the whole cache is stale.
      await qc.invalidateQueries();
    } catch (err) {
      if (isApiError(err) && err.fieldErrors.currentPassword) {
        setCurrentError(err.fieldErrors.currentPassword);
        throw new Error(t('errors.VALIDATION_FAILED'));
      }
      throw new Error(describeError(err, t));
    }
  };

  const signOut = async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
    qc.clear();
    await navigate({ to: '/login', search: {} });
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-lg items-center p-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t('auth.mustChangeTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('auth.mustChangeHelp')}</p>
          <PasswordForm
            submitLabel={t('auth.mustChangeSubmit')}
            onSubmit={submit}
            before={
              <Field label={t('auth.mustChangeCurrent')} error={currentError} required>
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                )}
              </Field>
            }
          />
          <Button type="button" variant="ghost" onClick={() => void signOut()}>
            {t('app.signOut')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
